import React from 'react';
import { LayoutDashboard, FileText, UserCircle, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SidebarProps {
    user: any;
    profile: any;
    activeTab: string;
    setActiveTab: (tab: string) => void;
    completion?: number;
}

const Sidebar: React.FC<SidebarProps> = ({ user, profile, activeTab, setActiveTab, completion = 0 }) => {
    const navigate = useNavigate();

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        navigate('/login');
    };

    const navItems = [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'profile', label: 'Profile', icon: UserCircle },
        { id: 'documents', label: 'Documents', icon: FileText },
    ];

    return (
        <div className="w-full md:w-64 min-h-screen p-6 flex flex-col fixed h-full overflow-y-auto"
            style={{ background: 'linear-gradient(180deg, #003434 0%, #002828 100%)' }}>

            {/* Profile Section */}
            <div className="flex flex-col items-center mb-8 pb-8"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="relative mb-4">
                    {/* SVG Progress Ring */}
                    <svg className="w-24 h-24 transform -rotate-90">
                        <circle cx="48" cy="48" r="44" stroke="rgba(255,255,255,0.06)" strokeWidth="4" fill="transparent" />
                        <circle
                            cx="48" cy="48" r="44"
                            strokeWidth="4" fill="transparent"
                            strokeDasharray={2 * Math.PI * 44}
                            strokeDashoffset={2 * Math.PI * 44 * (1 - completion / 100)}
                            strokeLinecap="round"
                            style={{ stroke: '#00e5d0' }}
                        />
                    </svg>

                    {/* Inner Profile Image/Initial */}
                    <div className="absolute inset-0 flex items-center justify-center p-2">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden"
                            style={{ background: 'rgba(255,255,255,0.06)' }}>
                            {profile?.ProfilePic ? (
                                <img src={profile.ProfilePic} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-2xl font-display font-bold" style={{ color: '#00e5d0' }}>
                                    {user?.FullName?.charAt(0) || user?.Username?.charAt(0)}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Completion Badge */}
                    <div className="absolute bottom-1 right-1 text-[10px] font-ui font-bold px-1.5 py-0.5 rounded-full"
                        style={{ background: '#00e5d0', color: '#003434', border: '2px solid #003434' }}>
                        {completion}%
                    </div>
                </div>

                <div className="text-center">
                    <h3 className="text-lg font-display font-bold text-white mb-0.5">
                        {user?.FullName || user?.Username}
                    </h3>
                    <p className="label-editorial mb-1" style={{ color: 'rgba(255,255,255,0.3)', letterSpacing: '0.15em' }}>
                        {profile?.StudentID || 'NEX-PENDING'}
                    </p>
                    <p className="text-[11px] font-ui font-bold uppercase tracking-widest" style={{ color: '#00e5d0' }}>
                        {profile?.TargetCountry || 'GLOBAL'}
                    </p>
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 space-y-1.5">
                {navItems.map(item => {
                    const isActive = activeTab === item.id;
                    return (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200"
                            style={{
                                background: isActive ? 'rgba(0, 229, 208, 0.12)' : 'transparent',
                                color: isActive ? '#00e5d0' : 'rgba(176, 204, 201, 0.5)',
                            }}
                            onMouseEnter={e => {
                                if (!isActive) {
                                    (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)';
                                    (e.currentTarget as HTMLElement).style.color = 'rgba(176, 204, 201, 0.7)';
                                }
                            }}
                            onMouseLeave={e => {
                                if (!isActive) {
                                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                                    (e.currentTarget as HTMLElement).style.color = 'rgba(176, 204, 201, 0.5)';
                                }
                            }}
                        >
                            <item.icon size={20} strokeWidth={isActive ? 2.2 : 1.8} />
                            <span className="text-sm font-ui font-bold">{item.label}</span>
                        </button>
                    );
                })}
            </nav>

            {/* Logout */}
            <button
                onClick={handleLogout}
                className="mt-8 flex items-center gap-3 px-4 py-3 rounded-xl transition-all group"
                style={{ color: 'rgba(186,26,26,0.6)' }}
                onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(186,26,26,0.06)';
                    (e.currentTarget as HTMLElement).style.color = 'rgba(186,26,26,0.8)';
                }}
                onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                    (e.currentTarget as HTMLElement).style.color = 'rgba(186,26,26,0.6)';
                }}
            >
                <LogOut size={20} className="transform group-hover:-translate-x-1 transition-transform" />
                <span className="text-sm font-ui uppercase tracking-wider font-bold">Log Out</span>
            </button>
        </div>
    );
};

export default Sidebar;
